import type { ClientInterface } from 'account-client';

/**
 * Shared notification state for the top bar bell and the portal banners, so both read
 * one list and a mark-as-read from either updates the other.
 */
export type UserNotification = {
  id: string;
  read_at: string | null;
  created_at_human: string;
  data: {
    title?: string;
    description?: string;
    icon?: string;
    icon_style?: string;
    /** Authored messages that should also show as a portal banner. */
    banner?: boolean;
    banner_style?: 'neutral' | 'critical' | null;
  };
};

export const useNotifications = () => {
  const { t, te } = useI18n();
  const toast = useToast();
  const client = useNuxtApp().$clientAccount as ClientInterface;
  const notifications = useState<UserNotification[]>('notifications', () => []);

  const unread = computed(() =>
    notifications.value.filter(item => !item.read_at)
  );
  const read = computed(() => notifications.value.filter(item => item.read_at));
  const banners = computed(() =>
    unread.value.filter(item => item.data.banner === true)
  );

  const fetchNotifications = async () => {
    const { data } = await client.notifications.list();
    notifications.value = data.data ?? [];
  };

  const markAsRead = async (notification: UserNotification) => {
    if (notification.read_at) return;

    // The GET is what marks it read server-side, which is why the SDK calls it this.
    await client.notifications.markAsRead(notification.id);
    // Update in place so the dot and the banners follow without a refetch.
    notification.read_at = new Date().toISOString();
  };

  const markAllAsRead = async () => {
    await client.notifications.markAllAsRead();
    notifications.value.forEach(item => {
      item.read_at = item.read_at ?? new Date().toISOString();
    });
  };

  /**
   * Permanently removes a read notification for this user only. The API rejects rows
   * that are still unread, so the panel only offers it on read ones.
   */
  const remove = async (notification: UserNotification) => {
    try {
      await client.notifications.delete(notification.id);
    } catch {
      toast.error(
        t('errors.error_default.title'),
        t('errors.error_default.description')
      );

      return;
    }

    notifications.value = notifications.value.filter(
      item => item.id !== notification.id
    );
  };

  /**
   * App-generated notifications carry i18n keys in their payload, authored ones carry
   * plain text.
   */
  const label = (value?: string) =>
    value && te(value) ? t(value) : (value ?? '');

  return {
    notifications,
    unread,
    read,
    banners,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    remove,
    label,
  };
};
